import React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import Accordion from 'LuxKit/Accordion'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Sheet from 'components/utils/Sheet'
import { useIntl } from 'react-intl'

const DesktopRowGrid = styled.div`
  display: grid;
  grid-template-columns: ${rem(200)} 1fr;
  align-items: start;
  gap: ${rem(24)};
  padding: ${rem(16)} 0;
`

/** Matches Accordion trigger padding for visual consistency */
const MobileStaticRow = styled(Group)`
  min-height: ${rem(48)};
  padding-block: ${rem(8)};
`

export interface ThingsToKnowRow {
  key: string
  icon: React.ReactElement
  label: string
  content: React.ReactNode
  collapsable: boolean
}

interface DesktopRowContentProps {
  row: ThingsToKnowRow
  showMoreText: string
  showLessText: string
}

function ThingsToKnowDesktopRow({ row, showMoreText, showLessText }: DesktopRowContentProps) {
  const desktopContent = row.collapsable
    ? row.content
    : <BodyText variant="medium" colour="neutral-two">{row.content}</BodyText>

  return (
    <DesktopRowGrid>
      <Group direction="horizontal" gap={8} verticalAlign="center">
        {row.icon}
        <BodyText variant="medium" weight="bold" colour="neutral-one">{row.label}</BodyText>
      </Group>
      <div>
        <LargeContentToggle maxHeight={80} showMoreText={showMoreText} showLessText={showLessText} toggleWeight="regular">
          {desktopContent}
        </LargeContentToggle>
      </div>
    </DesktopRowGrid>
  )
}

interface MobileRowProps {
  row: ThingsToKnowRow
  isLast: boolean
}

function ThingsToKnowMobileRow({ row, isLast }: MobileRowProps) {
  if (row.collapsable) {
    return (
      <Accordion heading={row.label} startIcon={row.icon}>
        {row.content}
      </Accordion>
    )
  }
  return (
    <>
      <MobileStaticRow direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <Group direction="horizontal" gap={8} verticalAlign="center">
          {row.icon}
          <BodyText variant="medium" weight="bold" colour="neutral-one">{row.label}</BodyText>
        </Group>
        <BodyText variant="medium" colour="neutral-two">{row.content}</BodyText>
      </MobileStaticRow>
      {!isLast && <Divider kind="secondary" direction="horizontal" />}
    </>
  )
}

interface Props {
  rows: Array<ThingsToKnowRow>
}

function ThingsToKnowLayout({ rows }: Props) {
  const intl = useIntl()

  if (!rows.length) return null

  const showMoreText = intl.formatMessage({
    id: 'EwlMSQQwsk',
    defaultMessage: 'Read more',
    description: 'Link text to expand content in things to know row',
  })
  const showLessText = intl.formatMessage({
    id: 'FAvqQN4xZM',
    defaultMessage: 'Read less',
    description: 'Link text to collapse content in things to know row',
  })

  return (
    <Sheet padding="8 0">
      <CSSBreakpoint min="tablet">
        {rows.map((row, index) => (
          <React.Fragment key={row.key}>
            <ThingsToKnowDesktopRow row={row} showMoreText={showMoreText} showLessText={showLessText} />
            {index < rows.length - 1 && <Divider kind="secondary" direction="horizontal" />}
          </React.Fragment>
        ))}
      </CSSBreakpoint>

      <CSSBreakpoint max="mobile">
        {rows.map((row, index) => (
          <ThingsToKnowMobileRow key={row.key} row={row} isLast={index === rows.length - 1} />
        ))}
      </CSSBreakpoint>
    </Sheet>
  )
}

export default ThingsToKnowLayout
